import React from "react";
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useSupabaseUser } from "@/hooks/use-supabase-user";

type LeaveRequest = {
  id: string;
  user_id: string;
  leave_type: string;
  status: string;
  reason: string | null;
  start_date: string;
  end_date: string;
  created_at: string;
  team_id?: string | null;
  profiles?: {
    first_name?: string | null;
    last_name?: string | null;
    full_name?: string | null;
    email?: string | null;
  };
};

interface ClientLeaveRequestsProps {
  statusFilter?: string;
}

const ClientLeaveRequests = ({ statusFilter }: ClientLeaveRequestsProps) => {
  const { toast } = useToast();
  const { user } = useSupabaseUser();

  const { data: leaveRequests, isLoading } = useQuery({
    queryKey: ["client-leave-requests", statusFilter, user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      try {
        // First get the teams assigned to this client
        const { data: clientTeams, error: teamsError } = await supabase
          .from("client_teams")
          .select("team_id")
          .eq("client_id", user.id);

        if (teamsError) throw teamsError;
        
        if (!clientTeams || clientTeams.length === 0) {
          return [];
        }
        
        const teamIds = clientTeams.map(t => t.team_id);
        
        // Then get the leave requests from those team members
        let query = supabase
          .from("leave_requests")
          .select("*")
          .in("team_id", teamIds)
          .order("created_at", { ascending: false });

        if (statusFilter && statusFilter !== 'all') {
          query = query.eq("status", statusFilter);
        }

        const { data: leaveData, error: leaveError } = await query;

        if (leaveError) throw leaveError;
        if (!leaveData || leaveData.length === 0) return [];

        // Then fetch profile data for each user_id
        const userIds = Array.from(new Set(leaveData.map(item => item.user_id).filter(Boolean)));
        
        let profilesMap: Record<string, any> = {};
        
        if (userIds.length > 0) {
          const { data: profilesData, error: profilesError } = await supabase
            .from("profiles")
            .select("id, first_name, last_name, full_name, email")
            .in("id", userIds);
          
          if (profilesError) throw profilesError;
          
          // Create a map of user_id -> profile data
          profilesMap = (profilesData || []).reduce((map, profile) => {
            map[profile.id] = profile;
            return map;
          }, {} as Record<string, any>);
        }
        
        // Combine leave data with profile data
        return leaveData.map(leave => ({
          ...leave,
          profiles: profilesMap[leave.user_id] || {
            first_name: null,
            last_name: null,
            full_name: "Unknown User",
            email: null
          }
        })) as LeaveRequest[];

      } catch (error: any) {
        toast({
          title: "Error loading leave requests",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }
    },
    enabled: !!user,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "approved":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      case "cancelled":
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
      default:
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    }
  };

  const getLeaveTypeLabel = (type: string) => {
    switch (type) {
      case "vacation":
        return "Vacation/Holiday";
      case "sick":
        return "Sick Leave";
      case "personal":
        return "Personal Leave";
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!leaveRequests || leaveRequests.length === 0 ? (
        <div className="text-center p-4">
          <p className="text-muted-foreground">No leave requests found for your team members</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {leaveRequests.map((request) => (
            <Card key={request.id} className="overflow-hidden bg-card border-border">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-md text-foreground">{getLeaveTypeLabel(request.leave_type)}</CardTitle>
                  <Badge className={getStatusColor(request.status)}>
                    {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="pb-2">
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Guard:</span>
                    <span className="font-medium text-foreground">
                      {request.profiles?.full_name || 
                        (request.profiles?.first_name && request.profiles?.last_name 
                          ? `${request.profiles.first_name} ${request.profiles.last_name}` 
                          : "Unknown")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">From:</span>
                    <span className="font-medium text-foreground">
                      {format(fromLocalISOString(request.start_date), "MMM d, yyyy")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">To:</span>
                    <span className="font-medium text-foreground">
                      {format(fromLocalISOString(request.end_date), "MMM d, yyyy")}
                    </span>
                  </div>
                  {request.reason && (
                    <div className="pt-2">
                      <p className="text-muted-foreground text-xs">Reason:</p>
                      <p className="text-sm text-foreground">{request.reason}</p>
                    </div>
                  )}
                </div>
              </CardContent>
              
              <CardFooter className="pt-2">
                <p className="text-xs text-muted-foreground">
                  Requested on {format(new Date(request.created_at), "MMM d, yyyy")}
                </p>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientLeaveRequests;